/* ==========================================================================
   ORKA — Yotpo star rating, restyled to match the buy-box `rating` block
   --------------------------------------------------------------------------
   orka-yotpo-rating.css handles everything CSS can reach. Two things it
   cannot:

     1. the star glyph — Yotpo draws its own 5-point star and fills it from a
        per-star <linearGradient> so half stars work. We keep that mechanism
        and only swap the viewBox, the path and the two gradient stop colours
        for the Orka star (snippets/orka-icon.liquid → `star`) on the Orka
        green / #d9d9d9 track.
     2. the underline — the native block underlines only the review count
        ("362+ reviews"), which needs its own element.

   The widget mounts after page load and Vue may re-render it, so a
   MutationObserver re-applies both. Every write is guarded by a read: if
   nothing actually changes we emit no mutations, so the observer settles
   instead of looping.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '.obb-info';
  var WIDGET = '.yotpo-reviews-star-ratings-widget';

  var STAR_VIEWBOX = '0 0 30.5 30.2';
  var STAR_PATH =
    'M23.6503 19.2936L29.858 13.0023C30.2772 12.583 30.4452 11.9117 30.2772 11.3246C30.1096 10.7373 29.606 10.318 28.9351 10.234L20.4627 8.9759L16.688 0.923034C16.4364 0.335796 15.8491 0.000183105 15.1779 0.000183105C14.5067 0.000183105 14.0034 0.335788 13.6678 0.923034L9.89307 8.9759L1.42061 10.234C0.833374 10.318 0.330086 10.7373 0.0784935 11.3246C-0.0891574 11.9118 -0.00548828 12.5827 0.497768 13.0023L6.70541 19.2936L5.19562 28.1856C5.11164 28.7729 5.36327 29.4437 5.86683 29.7793C6.11846 29.947 6.45407 30.1149 6.78968 30.1149C7.04131 30.1149 7.29294 30.031 7.54457 29.9473L15.0943 25.8369L22.6441 29.9473C23.2313 30.2829 23.9022 30.1989 24.4058 29.8633C24.9091 29.5277 25.1607 28.8568 25.077 28.2696L23.6503 19.2936Z';

  /* Write only on a real change — a no-op setAttribute still fires the
     observer, which would spin the rAF loop forever. */
  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function dropAttr(el, name) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  }

  function token(el, name, fallback) {
    var value = getComputedStyle(el).getPropertyValue(name).trim();
    return value || fallback;
  }

  function paintStar(svg, fill, track) {
    setAttr(svg, 'viewBox', STAR_VIEWBOX);
    /* Sized by CSS; the 14px width/height attributes would otherwise win over
       `height: auto`. */
    dropAttr(svg, 'width');
    dropAttr(svg, 'height');

    var stops = svg.querySelectorAll('linearGradient stop');
    for (var i = 0; i < stops.length; i++) {
      setAttr(stops[i], 'stop-color', i === 0 ? fill : track);
    }

    var paths = svg.querySelectorAll('path');
    for (var j = 0; j < paths.length; j++) {
      setAttr(paths[j], 'd', STAR_PATH);
      /* Yotpo outlines the star; the Orka star is solid. */
      setAttr(paths[j], 'stroke', 'none');
      if (!stops.length) {
        setAttr(
          paths[j],
          'fill',
          svg.classList.contains('yotpo-sr-star-empty') ? track : fill
        );
      }
    }
  }

  /* "based from 527 reviews" → "based from <u>527 reviews</u>" */
  function underlineCount(node) {
    if (node.querySelector('.orka-u')) return;

    var text = node.textContent.replace(/\s+/g, ' ').trim();
    var parts = text.match(/^(.*?)(\d[\d,.]*\+?\s*reviews?)(.*)$/i);
    if (!parts) return;

    var underline = document.createElement('span');
    underline.className = 'orka-u';
    underline.textContent = parts[2];

    node.textContent = '';
    if (parts[1]) node.appendChild(document.createTextNode(parts[1]));
    node.appendChild(underline);
    if (parts[3]) node.appendChild(document.createTextNode(parts[3]));
  }

  function restyle(widget) {
    var fill = token(widget, '--orka-star-color', '#00b054');
    var track = token(widget, '--orka-star-track', '#d9d9d9');

    var stars = widget.querySelectorAll('svg.star-container');
    for (var i = 0; i < stars.length; i++) paintStar(stars[i], fill, track);

    var texts = widget.querySelectorAll('.yotpo-sr-bottom-line-text');
    for (var j = 0; j < texts.length; j++) underlineCount(texts[j]);
  }

  /* Yotpo re-renders by patching the existing nodes — it rewrites viewBox,
     width, d and stroke on the same <svg> elements and refills the copy in
     place. Neither shows up as a childList record, so watching only for
     insertions means the first pass gets silently reverted a moment later.
     Each widget therefore gets its own observer that also hears attribute
     and text changes. Safe against feedback because every write in here is
     guarded by a read: once the widget matches, we emit no mutations. */
  var watched = typeof WeakSet === 'function' ? new WeakSet() : null;

  function watch(widget) {
    if (!watched || watched.has(widget)) return;
    watched.add(widget);
    new MutationObserver(schedule).observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });
  }

  function scan() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      var widgets = roots[i].querySelectorAll(WIDGET);
      for (var j = 0; j < widgets.length; j++) {
        restyle(widgets[j]);
        watch(widgets[j]);
      }
    }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      scan();
    });
  }

  function start() {
    scan();
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true
    });
    /* Theme-editor block reorder / re-render. */
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:block:select', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
